<template>
  <div class="c-profile__avatar-component">
    <div class="c-profile__picture-container">
      <SVGIcon class="c-profile__picture-border" width="48" height="48" name="avatar-outline" />
      <img :src="avatarUrl" alt="" class="c-profile__picture">
      <SVGIcon v-if="hasActiveMembership" class="c-profile__premium-icon" name="diamond-small" />
    </div>
    <h3 v-if="isSameUser" class="c-heading__icon avatar-edit__icon" @click.prevent="triggerInput">
      <SVGIcon class="c-button__icon" name="pen" />
    </h3>
    <div v-if="isError" class="c-message c-message--error c-message--error-avatar u-mbm">
      <p>{{ t('updateProfileError') }}</p>
      <p v-if="isTypeError">{{ t('imageTypeError') }}</p>
      <p v-if="isSizeError">{{ t('imageSizeError') }}</p>
      <p v-if="isNameError">{{ t('imageNameError') }}</p>
    </div>
    <input
      :id="`${randomID}-browse`"
      ref="input"
      class="u-hide"
      name="browse"
      type="file"
      @change="browsedFile"
    >
    <!-- submit -->
    <fieldset class="c-form__actions u-hide">
      <BtnSubmit id="button-submit-avatar" ref="buttonSubmit" class="c-profile__button-submit" :loading="submit.loading" :label="t('updateProfile')" />
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from '@/utils/i18n'
import { useRequest, updateDateModified, getUser } from '@/utils/api'
import { getCookie } from '@/utils/get-cookie'
import BtnSubmit from '@/components/buttons/BtnSubmit.vue'
import { formatDateTime } from '@/utils/format-date'
import SVGIcon from '@/components/SVGIcon.vue'

const props = defineProps<{
  userId: string,
  userName: string,
  profileId: number,
  profileName: string,
  avatar?: string
  avatarUrl: string
}>()

let userId: number
let userName: string
let profileId: number
let profileName: string

const profileUser = ref()
const hasActiveMembership = ref(false)

let isSameUser: boolean

let avatarUrl: string

setProfile()
setAvatarUrl()

const csrftoken = getCookie('csrftoken')!

let isUploaded: boolean = false
const isError = ref(false)
const isTypeError = ref(false)
const isSizeError = ref(false)
const isNameError = ref(false)

let editIcon: HTMLElement

const form: HTMLFormElement = document.querySelector('#avatar-form')!

const input = ref<HTMLInputElement>()
const randomID = Math.random().toString(36).substr(2, 9)
const file = ref<File>()

let buttonSubmit: HTMLButtonElement

onMounted(async () => {
  profileUser.value = await getUser(props.profileId)
  // console.log('profileUser: ', profileUser.value)
  hasActiveMembership.value = profileUser.value.forum_user.is_premium
  // console.log('hasActiveMembership: ', profileUser.value)

  if (isSameUser) {
    editIcon = document.querySelector('.avatar-edit__icon')!

    form?.addEventListener('submit', async (e) => {
      e.preventDefault()

      isUploaded = false
      isError.value = false
      isTypeError.value = false
      isSizeError.value = false

      // show loading icon
      editIcon.classList.add('avatar-loading__icon')

      const checkIcon = document.querySelector('.avatar-edit__icon .fa-pen')
      checkIcon?.classList.remove('fa-solid', 'fa-pen')
      checkIcon?.classList.add('fas', 'fa-spinner', 'fa-spin')

      const fd = new FormData()

      // @ts-ignore
      fd.append('user', profileId)
      fd.append('avatar', file.value!)

      const url = '/api/profile/' + userId + '/'

      await fetch(url, {
        credentials: 'include',
        method: 'PUT',
        mode: 'same-origin',
        headers: {
          'X-CSRFToken': csrftoken
        },
        body: fd
      }).then(response => {
        if (response.status === 200) {
          isUploaded = true
        } else {
          response.text().then(text => {
            if (text.includes('characters')) {
              isNameError.value = true
            }

            throw new Error(text)
          })

          isError.value = true
        }
      }).catch(error => {
        console.log(error)
      }).finally(async () => {
        if (isUploaded) {
          checkIcon?.parentElement?.classList.add('avatar-confirm__icon')

          const uploadButton = document.querySelector('.avatar-edit__icon')!
          uploadButton.classList.add('c-profile__icon-disabled')

          const currentDateTime = {
            user: profileId,
            date_modified: formatDateTime(new Date())
          }

          await updateDateModified(userId, currentDateTime)

          document.location.reload()
        } else {
          // show pen icon
          checkIcon?.classList.remove('fas', 'fa-spinner', 'fa-spin')
          checkIcon?.classList.add('fa-solid', 'fa-pen')
        }
      })
    })
  }
})

function setProfile () {
  userId = parseInt(props.userId)
  profileId = props.profileId

  userName = props.userName
  profileName = props.profileName

  isSameUser = userName === profileName
}

async function setAvatarUrl () {
  avatarUrl = props.avatarUrl
}

// The hidden input is triggered when clicking on edit icon
// it calls the browsedFile function that get the input file and
// click the hidden button to submit the form
function triggerInput () {
  const i = input
  i.value?.click()
}

function browsedFile (e: Event) {
  const input = e.target as HTMLInputElement
  file.value = input.files![0]

  if (file.value.type === 'image/jpeg' || file.value.type === 'image/png') {
    if (file.value.size < 4 * 1024 * 1024) {
      buttonSubmit = <HTMLButtonElement>document.getElementById('button-submit-avatar')
      buttonSubmit.click()
    } else {
      isError.value = true
      isSizeError.value = true
    }
  } else {
    isError.value = true
    isTypeError.value = true
  }
}

const submit = useRequest(async () => {

},
{
  error () {
    // scroll to error message when an error occurs
    if (form.value) {
      form.value.scrollIntoView({ behavior: 'smooth' })
    }
  }
})

const { t } = useI18n({
  messages: {
    en: {
      updateProfile: 'Update',
      updateProfileError: 'Error while updating profile.',
      removeAndRetry: 'Please remove it and retry.',
      imageTypeError: 'Image must be in .jpg or .png format.',
      imageSizeError: 'Image size is too big.',
      imageNameError: 'File name is too long (100 characters maximum).',
      cancel: 'Cancel the upload',
      remove: 'Remove'
    },
    fr: {
      updateProfile: 'Mettre à jour',
      updateProfileError: 'Erreur lors de la mise à jour du profil.',
      removeAndRetry: 'Merci de le supprimer puis de réessayer.',
      imageTypeError: 'Veuillez uploader une image au format .jpg ou .png',
      imageSizeError: 'Le fichier est trop volumineux.',
      imageNameError: 'Le nom du fichier est trop long (100 caractères maximum.)',
      cancel: 'Annuler le téléversement',
      remove: 'Supprimer'
    }
  }
})
</script>
